import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, MessageCircle, BookOpen } from "lucide-react";
import { BACKEND, api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export function RichText({ text }) {
  return (
    <div className="space-y-1.5">
      {text.split("\n").filter((l, i, a) => l.trim() || (i > 0 && a[i - 1].trim())).map((line, i) => {
        const html = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
        const bullet = /^\s*([-*•]|\d+\.)\s+/.test(line);
        return <p key={i} className={bullet ? "pl-3" : ""} dangerouslySetInnerHTML={{ __html: bullet ? "• " + html.replace(/^\s*([-*•]|\d+\.)\s+/, "") : html }} />;
      })}
    </div>
  );
}

function ContactForm({ sessionId, onDone }) {
  const { t } = useSite();
  const saved = JSON.parse(localStorage.getItem("rk_chat_contact") || "{}");
  const [name, setName] = useState(saved.name || "");
  const [phone, setPhone] = useState(saved.phone || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return setErr(t("chat.errName"));
    if (phone.replace(/\D/g, "").length < 9) return setErr(t("chat.errPhone"));
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/chat/${sessionId}/contact`, { name: name.trim(), phone: phone.trim() });
      localStorage.setItem("rk_chat_contact", JSON.stringify({ name: name.trim(), phone: phone.trim() }));
      onDone(data);
    } catch (er) {
      setErr(errMsg(er));
    } finally {
      setBusy(false);
    }
  };
  const cls = "w-full border border-emerald-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500";
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="chatbot-contact-form">
      <p className="text-xs text-emerald-900">{t("chat.contactIntro")}</p>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("chat.name")} maxLength={80} className={cls} data-testid="chatbot-contact-name" />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("chat.phone")} inputMode="tel" maxLength={20} className={cls} data-testid="chatbot-contact-phone" />
      {err && <p className="text-[11px] text-rose-600" data-testid="chatbot-contact-error">{err}</p>}
      <button disabled={busy} className="w-full bg-[#0F2B48] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#D97706] disabled:opacity-60" data-testid="chatbot-contact-submit">
        {busy ? t("chat.saving") : t("chat.continue")}
      </button>
    </form>
  );
}

function Escalation({ esc, sessionId, onUpdate }) {
  const { t } = useSite();
  if (!esc?.wa_link) return null;
  const click = () => api.post(`/chat/${sessionId}/handoff-click`).catch(() => {});
  return (
    <div className="mt-3 border border-emerald-200 bg-emerald-50 p-3" data-testid="chatbot-escalation-card">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-emerald-700">{t("chat.detected")}: {esc.trigger.join(" · ")}</p>
      {esc.needs_contact ? <ContactForm sessionId={sessionId} onDone={onUpdate} /> : <>
      <a href={esc.wa_link} target="_blank" rel="noreferrer" onClick={click} data-testid="chatbot-wa-escalation-button"
        className="flex items-center justify-center gap-2 bg-[#10B981] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600">
        <MessageCircle size={16} /> {t("chat.wa")}
      </a>
      <p className="mt-2 text-[11px] text-emerald-800">{t("chat.attached")} {esc.admin_name}.</p>
      </>}
    </div>
  );
}

export default function ChatWidget() {
  const { t, lang } = useSite();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", greeting: true, content: "" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem("rk_chat_sid"));
  const endRef = useRef(null);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("open-chat", h);
    return () => window.removeEventListener("open-chat", h);
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", pending: true }]);
    let curSid = sid;
    let gotDone = false;
    let answer = "";
    const recover = async () => {
      if (!curSid) return false;
      for (let i = 0; i < 6; i++) {
        try {
          const { data } = await api.get(`/chat/${curSid}/last`);
          if (data?.content) {
            patchLast((l) => ({ ...l, pending: false, content: data.content, escalation: data.escalation, sources: [...new Set(data.sources)] }));
            return true;
          }
        } catch { /* retry */ }
        await new Promise((r) => setTimeout(r, 2500));
      }
      return false;
    };
    try {
      const res = await fetch(`${BACKEND}/api/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "text/event-stream" }, body: JSON.stringify({ message, session_id: sid, lang }) });
      if (!res.ok || !res.body) {
        const detail = await res.json().then((j) => j.detail).catch(() => null);
        throw new Error(typeof detail === "string" ? detail : t("chat.unavailable"));
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      const handle = (p) => {
        if (!p.startsWith("data: ")) return;
        let ev;
        try { ev = JSON.parse(p.slice(6)); } catch { return; }
        if (ev.type === "session") { curSid = ev.session_id; setSid(ev.session_id); localStorage.setItem("rk_chat_sid", ev.session_id); }
        if (ev.type === "delta") { answer += ev.content; patchLast((l) => ({ ...l, content: l.content + ev.content })); }
        if (ev.type === "done") { gotDone = true; patchLast((l) => ({ ...l, pending: false, escalation: ev.escalation, sources: [...new Set(ev.sources)] })); }
      };
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach(handle);
      }
      if (buf.trim()) handle(buf.trim());
      if (!gotDone || !answer.trim()) {
        const ok = await recover();
        if (!ok) patchLast((l) => (l.content ? { ...l, pending: false } : { ...l, pending: false, content: t("chat.noAnswer") }));
      }
    } catch (er) {
      const ok = await recover();
      if (!ok) patchLast((l) => ({ ...l, pending: false, content: er?.message || t("chat.conn") }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chatbot-open-button"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#0F2B48] py-3 pl-3 pr-5 text-white shadow-2xl transition-transform hover:-translate-y-1">
          <span className="grid h-10 w-10 place-items-center bg-[#D97706]"><Bot size={20} /></span>
          <span className="text-left text-sm font-semibold leading-tight">Virtual Assistant<span className="block font-mono text-[10px] font-normal text-emerald-300">● Online</span></span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-0 right-0 z-50 flex h-[100dvh] w-full flex-col border border-slate-200 bg-white shadow-2xl sm:bottom-6 sm:right-6 sm:h-[640px] sm:w-[400px] fade-up" data-testid="chatbot-window">
          <div className="flex items-center justify-between bg-[#0F2B48] px-5 py-4 text-white">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center bg-[#D97706]"><Bot size={18} /></span>
              <div><p className="text-sm font-bold">Virtual Assistant</p><p className="font-mono text-[10px] uppercase tracking-widest text-slate-300">PT Rikonstruksi Utama</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close-button"><X size={18} /></button>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto bg-[#FAF9F6] p-4 blueprint-grid" data-testid="chatbot-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chatbot-message-${m.role}-${i}`}>
                <div className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0F2B48] text-white" : "border border-slate-200 bg-white text-slate-800"}`}>
                  {m.pending && !m.content ? <span className="flex gap-1">{[0, 1, 2].map((d) => <span key={d} className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />)}</span> : <RichText text={m.greeting ? t("chat.greeting") : m.content} />}
                  {m.sources?.length > 0 && !m.escalation && (
                    <p className="mt-2 flex items-center gap-1 border-t border-slate-100 pt-2 font-mono text-[10px] text-slate-400"><BookOpen size={10} /> {m.sources.length} {t("chat.sources")}</p>
                  )}
                  <Escalation esc={m.escalation} sessionId={sid} onUpdate={(e) => setMsgs((ms) => ms.map((x, j) => (j === i ? { ...x, escalation: e } : x)))} />
                </div>
              </div>
            ))}
            {msgs.length === 1 && (
              <div className="flex flex-wrap gap-2">
                {t("chat.q").map((q, i) => <button key={q} onClick={() => send(q)} data-testid={`chatbot-quick-${i}`} className="border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition-colors hover:border-[#D97706] hover:text-[#D97706]">{q}</button>)}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex border-t border-slate-200">
            <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000} placeholder={t("chat.placeholder")} data-testid="chatbot-input"
              className="flex-1 px-4 py-4 text-sm outline-none" />
            <button disabled={busy || !input.trim()} data-testid="chatbot-send-button" className="bg-[#D97706] px-5 text-white transition-colors hover:bg-[#B45309] disabled:opacity-40"><Send size={16} /></button>
          </form>
        </div>
      )}
    </>
  );
}
